import { errorText } from '@/api/client';
import { useShifts, useUsers } from '@/api/data';
import { useLabour, useSales, useSaveLabour, useSaveSales } from '@/api/labour';
/**
 * Lønnsprosent, looking back (0017 P0.5; renamed 2026-09-27, planning moved into the Vaktplan's
 * Bemanning mot behov row): wage cost per day against the sales the
 * manager types, against a target. Sales, target and rates are the company's, on the server since
 * 2026-09-25 (api/labour.ts); they were in each browser before.
 *
 * Since the manager redesign (2026-10-09) it is the Lønnsprosent tab of Timer til lønn: the same
 * header, its own week or month and its own company (the active one), and its own export. A day with
 * no sales is missing, not zero; 0 is a day that took nothing; a figure under 1 000 is a unit slip
 * to check. It is the plan's cost, not the pay run's.
 *
 * A department (`?avdeling=<team id>`, the person's first team by name as Vaktplan groups them) narrows
 * the cost to its people. Sales are stored per company only, so with a department chosen the page shows
 * the department's cost and no percentage, and says why (owner 2026-11-04).
 */
import { T } from '@/i18n';
import { breakPolicy, isPunched, oslo, plannedHours, plannedPaidHours, today, worked } from '@/rules';
import { type Paid, costOf, hourlyRateOf, ratesOf } from '@/rules/labour';
import { LocalFiguresOffer } from '@/shell/LocalFiguresOffer';
import { useTenant } from '@/shell/tenant';
import { Button, LoadError, Skeleton, toast } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { monthLabel } from '@/ui/format';
import { DNL, fmtDs, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Explain, Notice, TabPanel } from '@/ui/page';
import { addDays, dow, isoWeek, mondayOf, weekDays } from '@timefly/core';
import { Link, useSearchParams } from 'react-router-dom';
import { LonnHead } from './lonn/Head';

const kr = (n: number) => Math.round(n).toLocaleString('nb-NO');

/** Every day of a YYYY-MM, as YYYY-MM-DD. */
export function monthDays(ym: string): string[] {
  const [y, m] = ym.split('-').map(Number);
  const n = new Date(Date.UTC(y ?? 2026, m ?? 1, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${ym}-${String(i + 1).padStart(2, '0')}`);
}

/** Steps months without ever landing on the 31st of a short one. */
export function shiftMonth(ym: string, by: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// the shared month label, re-exported for the pages that import it from here
export { monthLabel };

/** What a day's sales say: none typed, a typed 0, a figure under 1 000 to check, or one to count. */
export type SalesState = 'missing' | 'zero' | 'odd' | 'ok';
export const salesState = (v: number | undefined): SalesState =>
  v == null ? 'missing' : v === 0 ? 'zero' : v < 1000 ? 'odd' : 'ok';

/** The figure typed in a cell: empty clears the day (null), anything else is the kroner typed. */
export const typedSales = (raw: string): number | null => {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const v = Number(t);
  return Number.isFinite(v) && v >= 0 ? v : null;
};

type Basis = 'planned' | 'recorded' | 'approved';
const BASIS: Record<Basis, string> = { planned: 'Planlagt', recorded: 'Registrert', approved: 'Godkjent' };
/** the clocked span in hours, before any break; a pair out of order is no time */
const spanOf = (s: { punch_in_time?: string | null; punch_out_time?: string | null }) =>
  s.punch_in_time && s.punch_out_time
    ? Math.max(0, (Date.parse(s.punch_out_time) - Date.parse(s.punch_in_time)) / 3_600_000)
    : 0;
type Sh = Parameters<typeof plannedHours>[0];
type Pol = Parameters<typeof plannedPaidHours>[1];
/** The hours a row shows for the basis: planned and recorded before unpaid breaks, approved after them. */
const hoursOf = (b: Basis, s: Sh, p: Pol) =>
  b === 'planned' ? plannedHours(s) : b === 'recorded' ? spanOf(s) : (worked(s, p) ?? 0);
/** The hours the cost is paid on, after the company's unpaid break, for every basis. */
const paidOf = (b: Basis, s: Sh, p: Pol) => (b === 'planned' ? plannedPaidHours(s, p) : (worked(s, p) ?? 0));
/**
 * The person's rates on the shift's day (rate history, 2026-11-04): the older rate the API sends with the
 * shift when one applies, else the person's own, so a day before a raise costs what it was paid.
 */
export const paidOn = (s: {
  user?: unknown;
  rate_on_day?: { hourly_rate: string | null; monthly_rate: string | null } | undefined;
}): Paid | null =>
  s.rate_on_day
    ? {
        ...(s.user as Paid | null),
        hourly_rate: s.rate_on_day.hourly_rate,
        monthly_rate: s.rate_on_day.monthly_rate,
      }
    : ((s.user as Paid | null) ?? null);

import { TRIPLETEX } from './lonn/payrollSystem';
import { PowerOfficeRevenue } from './omsetning/PowerOfficeRevenue';
import { isUnplanned } from './oversikt/day';
import { deptOf } from './vaktplan/departments';

export default function Omsetning() {
  const { tenant, mode } = useTenant();
  // the period, its span and the basis live in the address, so Back from a day link returns to the same view (#272 O-R02)
  const [sp, setSp] = useSearchParams();
  const put = (k: string, v: string) =>
    setSp(
      (n) => {
        n.set(k, v);
        return n;
      },
      { replace: true },
    );
  const askedWeek = sp.get('uke') ?? '';
  const monday = /^\d{4}-\d{2}-\d{2}$/.test(askedWeek) ? mondayOf(askedWeek) : mondayOf(today());
  const setMonday = (d: string) => put('uke', d);
  // "Labor dabar galima pasitikrinti tik weekly, bet reikėtų ir monthly varianto" (designer's
  // report, 2026-09-21). A month shows every day rather than a weekly summary, because sales
  // are typed in per day and a summary would take the entry away.
  const span: 'week' | 'month' = sp.get('periode') === 'maned' ? 'month' : 'week';
  const setSpan = (v: 'week' | 'month') => put('periode', v === 'month' ? 'maned' : 'uke');
  const askedMonth = sp.get('mnd') ?? '';
  const ym = /^\d{4}-(0[1-9]|1[0-2])$/.test(askedMonth) ? askedMonth : today().slice(0, 7);
  const setYm = (v: string) => put('mnd', v);
  // which hours the cost is read from (#263, V6): the plan, the clocked pairs, or the approved worked time
  const asked = sp.get('grunnlag');
  const basis: Basis = asked === 'registrert' ? 'recorded' : asked === 'godkjent' ? 'approved' : 'planned';
  const setBasis = (b: Basis) =>
    put('grunnlag', b === 'recorded' ? 'registrert' : b === 'approved' ? 'godkjent' : 'planlagt');
  // the department, by id, among the teams the people have; anything else is the whole company
  const usersQ = useUsers();
  const teams = [
    ...new Map(
      (usersQ.data ?? []).flatMap((u) => (u.teams ?? []).filter((t) => t.name).map((t) => [t.id, t])),
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name, 'nb'));
  const askedDept = sp.get('avdeling') ?? '';
  const dept = /^\d{1,9}$/.test(askedDept) ? (teams.find((t) => t.id === Number(askedDept)) ?? null) : null;
  const setDept = (v: string) =>
    setSp(
      (n) => {
        if (v) n.set('avdeling', v);
        else n.delete('avdeling');
        return n;
      },
      { replace: true },
    );
  const inDept = new Set(
    dept ? (usersQ.data ?? []).filter((u) => deptOf(u)?.id === dept.id).map((u) => u.id) : [],
  );
  const days = span === 'week' ? weekDays(monday) : monthDays(ym);
  const from = days[0] ?? monday;
  const to = days[days.length - 1] ?? monday;
  const labour = useLabour();
  const rates = ratesOf(labour.rates);
  const shiftsQ = useShifts(from, to);
  const salesQ = useSales(from, to);
  const { sales } = salesQ;
  const target = labour.target;
  const saveSales = useSaveSales();
  const saveLabour = useSaveLabour();
  // the plan only (#263): a clock-in with no planned shift has no planned hours, as on Dagen; recorded and
  // approved read every clocked pair, unplanned clock-ins included, since those are worked time
  const all = (shiftsQ.data?.shifts ?? []).filter(
    (s) => s.user_id != null && (!dept || inDept.has(s.user_id)),
  );
  const shifts =
    basis === 'planned'
      ? all.filter((s) => !isUnplanned(s))
      : basis === 'recorded'
        ? all.filter(isPunched)
        : all.filter((s) => s.is_approved && isPunched(s));
  // every figure waits for the plan, the rates and the sales; until then it is unknown, not 0 (#262, #263)
  const known =
    shiftsQ.isSuccess &&
    !labour.pending &&
    !labour.failed &&
    salesQ.ready &&
    (!askedDept || usersQ.isSuccess);
  const failed: { what: string; retry: () => unknown }[] = [];
  if (shiftsQ.isError) failed.push({ what: T('vaktplanen'), retry: () => shiftsQ.refetch() });
  if (labour.failed) failed.push({ what: T('lønnsinnstillingene'), retry: labour.retry });
  if (salesQ.failed) failed.push({ what: T('omsetningen'), retry: salesQ.retry });
  if (askedDept && usersQ.isError) failed.push({ what: T('de ansatte'), retry: () => usersQ.refetch() });
  const dash = (v: string) => (known ? v : '–');
  const rota = mode === 'rota';
  const policy = breakPolicy(tenant ?? undefined);

  const rows = days.map((d) => {
    // a shift counts on the day it starts, a night into the next day included, cost and hours alike
    const ss = shifts.filter((s) => (oslo(s.start_time)?.date ?? '') === d);
    // Gross pay first, then pension and the employer's contribution on top (rules/labour.ts).
    // This used to be `hourly_rate ?? 0` times a hardcoded 1.141: no pension at all, and a
    // monthly-salaried person counted as free (designer's report, 2026-09-21).
    // each shift at the rates of its own day when an older rate applies (rate history, 2026-11-04)
    const wage = ss.reduce((a, s) => a + paidOf(basis, s, policy) * hourlyRateOf(paidOn(s)), 0);
    const cost = costOf(wage, rates).total;
    const sl = sales[d];
    const state = salesState(sl);
    return {
      d,
      // planned: start to end before the break, a punch never changes it (#263); recorded: the clocked span
      // before the break; approved: the worked time after the break, as Lønnsgrunnlag counts it
      h: ss.reduce((a, s) => a + hoursOf(basis, s, policy), 0),
      cost,
      sales: sl,
      state,
      // a figure under 1 000 kr is a unit slip ("1" meant 1 kr and gave 876 298 %, owner 2026-09-27):
      // no percentage, and it says to check the amount
      // a department has no sales of its own: no percentage (owner 2026-11-04)
      pct: known && !dept && state === 'ok' && sl != null && ss.length > 0 ? (cost / sl) * 100 : null,
      // no shifts on the basis: no cost to set against the sales, so the day stays out of the ratio (#263 O-01)
      none: ss.length === 0,
      future: d > today(),
    };
  });
  // people on the basis with no hourly or monthly rate: their hours are in the cost at 0 kr, which is
  // said and named rather than read as a complete figure (#263: missing inputs are unknown, not zero)
  const inPeriod = shifts.filter((s) => {
    const d = oslo(s.start_time)?.date ?? '';
    return d >= from && d <= to;
  });
  const unpriced = [
    ...new Map(
      inPeriod
        .filter((s) => hourlyRateOf(paidOn(s)) === 0)
        .map((s) => [s.user_id as number, s.user as { name?: string; lastname?: string } | null]),
    ),
  ];
  const tc = rows.reduce((a, r) => a + r.cost, 0);
  const ts = rows.reduce((a, r) => a + (r.sales ?? 0), 0);
  // the period's share over the days that have real sales only: a week's cost against one day's
  // sales read 5 738 687 % (owner, 2026-09-27)
  const counted = rows.filter((r) => r.pct != null);
  const cc = counted.reduce((a, r) => a + r.cost, 0);
  const cs = counted.reduce((a, r) => a + (r.sales ?? 0), 0);
  const wk = cs ? (cc / cs) * 100 : null;
  // a day that has not come yet cannot be missing its sales (#272 O-R03)
  const missing = rows.filter((r) => r.state === 'missing' && !r.future).length;
  const odd = rows.filter((r) => r.state === 'odd').length;
  const setSales = (d: string, v: number | null) =>
    saveSales.mutate(
      { [d]: v },
      {
        onSuccess: () =>
          toast(
            v == null
              ? T('Omsetning fjernet for {day}', { day: `${DNL[dow(d)]} ${fmtDs(d)}` })
              : T('Omsetning lagret for {day}', { day: `${DNL[dow(d)]} ${fmtDs(d)}` }),
          ),
        onError: (e) => toast(errorText(e)),
      },
    );
  const setTarget = (v: number) =>
    saveLabour.mutate(
      { revision: labour.revision, wage_target_pct: v },
      { onError: (e) => toast(errorText(e)) },
    );
  const period = span === 'week' ? `${T('Uke')} ${isoWeek(monday)}` : monthLabel(ym);

  /** The table as it stands, as a file: the days, the plan's cost, the sales and their state. */
  const exportCsv = () => {
    // the file says what the table says: nothing while a figure is still unknown (#262)
    if (!known) return toast(T('Vent til tallene er hentet.'));
    const state = {
      missing: 'Mangler',
      zero: 'Registrert 0',
      odd: 'Sjekk beløpet',
      ok: 'Lagt inn',
    };
    const lines = [
      [
        'Dato',
        `${BASIS[basis]}e timer`,
        `${BASIS[basis]} lønnskostnad (kr)`,
        'Omsetning ekskl. mva (kr)',
        'Omsetning',
        'Lønn %',
      ],
      ...rows.map((r) => [
        r.d,
        r.h.toFixed(2).replace('.', ','),
        String(Math.round(r.cost)),
        r.sales == null ? '' : String(r.sales),
        state[r.state],
        r.pct == null ? '' : r.pct.toFixed(1).replace('.', ','),
      ]),
    ];
    const csv = `\ufeff${lines.map((l) => l.join(';')).join('\n')}\n`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `lonnsprosent-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast(T('Lønnsprosent lastet ned'));
  };

  const head = (
    <LonnHead
      tab="kost"
      action={
        // a report's file is a quiet action; the page reads first (V6)
        <Button ghost onClick={exportCsv} disabled={!tenant || !rota || !known}>
          <Icon name="download" />
          {T('Eksporter lønnsprosent')}
        </Button>
      }
    />
  );
  if (!tenant)
    return (
      <div className="lonn oms">
        {head}
        <TabPanel tab="kost">
          <Skeleton />
        </TabPanel>
      </div>
    );
  if (!rota)
    return (
      <div className="lonn oms">
        {head}
        <TabPanel tab="kost">
          <Notice tone="info" title={T('Lønnsprosent krever en vaktplan')}>
            {T(
              'Lønnsprosent per dag krever omsetningstall og en plan. Denne bedriften har ingen av delene her.',
            )}{' '}
            <Link to="/timeliste">
              <Icon name="clock" />
              {T('Til timelisten')}
            </Link>
            .
          </Notice>
        </TabPanel>
      </div>
    );

  // the days left out of the ratio, named in the caption (V6: both sides use the same covered days)
  const left = rows.filter((r) => r.pct == null);
  const leftNames = left.slice(0, 3).map((r) => `${DNL[dow(r.d)]} ${fmtDs(r.d)}`);
  const names = (rs: typeof rows) =>
    rs.length > 3
      ? `${rs
          .slice(0, 3)
          .map((r) => `${DNL[dow(r.d)]} ${fmtDs(r.d)}`)
          .join(', ')} ${T('og {n} til', { n: rs.length - 3 })}`
      : rs.map((r) => `${DNL[dow(r.d)]} ${fmtDs(r.d)}`).join(', ');
  const leftText =
    left.length > 3
      ? `${leftNames.join(', ')} ${T('og {n} til', { n: left.length - 3 })}`
      : leftNames.join(', ');
  const label = T(BASIS[basis]);
  const pauses =
    basis === 'approved'
      ? T('Tid og kostnad etter ubetalte pauser')
      : T('Tid før ubetalte pauser, kostnad etter');
  const pctText = (v: number) => `${(Math.round(v * 10) / 10).toFixed(1).replace('.', ',')} %`;

  return (
    <div className="lonn oms">
      {head}
      <TabPanel tab="kost">
        <LocalFiguresOffer />
        <div className="mbar">
          <fieldset className="lseg" aria-label={T('Periode')}>
            {(['week', 'month'] as const).map((v) => (
              <button
                type="button"
                key={v}
                aria-pressed={span === v}
                onClick={() => setSpan(v)}
                data-testid={`span-${v}`}
              >
                {v === 'week' ? T('Uke') : T('Måned')}
              </button>
            ))}
          </fieldset>
          <PeriodControl
            period={span === 'week' ? 'week' : 'month'}
            month={span !== 'week'}
            value={span === 'week' ? monday : ym}
            onChange={(v) => (span === 'week' ? setMonday(mondayOf(v)) : setYm(v))}
            prevLabel={span === 'week' ? T('Forrige uke') : T('Forrige måned')}
            nextLabel={span === 'week' ? T('Neste uke') : T('Neste måned')}
            onPrev={() => (span === 'week' ? setMonday(addDays(monday, -7)) : setYm(shiftMonth(ym, -1)))}
            onNext={() => (span === 'week' ? setMonday(addDays(monday, 7)) : setYm(shiftMonth(ym, 1)))}
            extra={
              (span === 'week' ? monday !== mondayOf(today()) : ym !== today().slice(0, 7)) && (
                <Button
                  ghost
                  onClick={() => {
                    setSp(
                      (n) => {
                        n.set('uke', mondayOf(today()));
                        n.set('mnd', today().slice(0, 7));
                        return n;
                      },
                      { replace: true },
                    );
                  }}
                >
                  {T('I dag')}
                </Button>
              )
            }
          >
            <span data-testid="period">
              {span === 'week' ? (
                <>
                  {period}
                  <span className="lrange">
                    , {fmtDs(monday)}–{fmtDs(addDays(monday, 6))}
                  </span>
                </>
              ) : (
                period
              )}
            </span>
          </PeriodControl>
        </div>
        {/* planned, recorded or approved: title, formula, coverage and rows follow it together (#263 V5) */}
        <div className="lbasis">
          <fieldset className="lseg" aria-label={T('Beregningsgrunnlag')}>
            {(Object.keys(BASIS) as Basis[]).map((k) => (
              <button
                type="button"
                key={k}
                aria-pressed={basis === k}
                onClick={() => setBasis(k)}
                data-testid={`basis-${k}`}
              >
                {T(BASIS[k])}
              </button>
            ))}
          </fieldset>
          <span className="lpause">{pauses}</span>
          {teams.length > 0 && (
            <label className="ltgt">
              {T('Avdeling')}
              <select
                className="inp"
                value={dept ? String(dept.id) : ''}
                onChange={(e) => setDept(e.target.value)}
                data-testid="dept"
              >
                <option value="">{T('Hele bedriften')}</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="ltgt">
            {T('Mål')}
            <input
              className="inp ltarget"
              aria-label={T('Mål lønnsprosent')}
              type="number"
              min={1}
              max={100}
              key={target}
              defaultValue={target}
              onBlur={(e) =>
                Number(e.target.value) > 0 &&
                Number(e.target.value) !== target &&
                setTarget(Number(e.target.value))
              }
            />
            %
          </label>
        </div>
        {failed.length > 0 && (
          <LoadError
            what={failed.map((f) => f.what).join(', ')}
            onRetry={() => {
              for (const f of failed) f.retry();
            }}
          />
        )}
        {/* the calculation as one statement: cost ÷ sales = share, on the same covered days (V6) */}
        <section className="lcalcs" aria-labelledby="lcalc-h">
          <h2 id="lcalc-h">{T('{basis} lønnsprosent', { basis: label })}</h2>
          <div className="lsum">
            {!known ? (
              <output aria-busy={failed.length === 0}>
                <b>
                  {failed.length
                    ? T('Lønnsprosent ikke bekreftet')
                    : T('Henter tallene for {period}', { period })}
                </b>
              </output>
            ) : dept ? (
              <div className="leq">
                <div className="lterm">
                  <span>{T('Lønnskostnad, {name}', { name: dept.name })}</span>
                  <b data-testid="dept-cost">{kr(tc)} kr</b>
                </div>
              </div>
            ) : wk == null ? (
              <b>
                {rows.every((r) => r.state !== 'ok')
                  ? T('Ingen omsetning lagt inn')
                  : T('Ingen dager med både {basis} tid og omsetning', { basis: label.toLowerCase() })}
              </b>
            ) : (
              <div className="leq">
                <div className="lterm">
                  <span>{T('Lønnskostnad')}</span>
                  <b>{kr(cc)} kr</b>
                </div>
                <span className="lop" aria-hidden="true">
                  ÷
                </span>
                <div className="lterm">
                  <span>{T('Omsetning')}</span>
                  <b>{kr(cs)} kr</b>
                </div>
                <span className="lop" aria-hidden="true">
                  =
                </span>
                <div className="lres">
                  <b data-testid="week-pct">{pctText(wk)}</b>
                  <span>
                    {T('av omsetningen')}, {T('mål {n} %', { n: target })}
                  </span>
                </div>
              </div>
            )}
          </div>
          {dept && (
            <p className="lcover" data-testid="dept-note">
              <Icon name="info" />
              {T(
                'Omsetningen lagres for hele bedriften, ikke per avdeling, så avdelingen vises uten lønnsprosent.',
              )}
            </p>
          )}
          {known && wk != null && (
            <p className="lcover">
              <Icon name="info" />
              {left.length
                ? left.every((r) => r.state !== 'ok') || left.every((r) => r.state === 'ok')
                  ? T(
                      left.every((r) => r.state !== 'ok')
                        ? '{a} av {b} dager. Begge beløp utelater {days}, som mangler omsetning.'
                        : '{a} av {b} dager. Begge beløp utelater {days}, som mangler vakter.',
                      { a: counted.length, b: rows.length, days: leftText },
                    )
                  : // both reasons: each day under its own (#263 O-25)
                    T(
                      '{a} av {b} dager. Begge beløp utelater {sales}, som mangler omsetning, og {shifts}, som mangler vakter.',
                      {
                        a: counted.length,
                        b: rows.length,
                        sales: names(left.filter((r) => r.state !== 'ok')),
                        shifts: names(left.filter((r) => r.state === 'ok')),
                      },
                    )
                : T('Alle {b} dagene har omsetning.', { b: rows.length })}
              {odd ? ` ${T('{n} må kontrolleres.', { n: odd })}` : ''}
            </p>
          )}
          {known && unpriced.length > 0 && (
            <p className="lcover" data-testid="unpriced">
              <Icon name="warn" />
              <span>
                {T('Kostnaden er ufullstendig: {n} uten timelønn eller månedslønn er regnet til 0 kr.', {
                  n: unpriced.length === 1 ? T('1 ansatt') : T('{n} ansatte', { n: unpriced.length }),
                })}{' '}
                {unpriced.map(([id, u], i) => (
                  <span key={id}>
                    {i > 0 && ', '}
                    <Link to={`/ansatte/${id}?rediger=hourly_rate`}>
                      {[u?.name, u?.lastname].filter(Boolean).join(' ') || T('Ukjent')}
                    </Link>
                  </span>
                ))}
              </span>
            </p>
          )}
        </section>
        {/* the accounts' revenue for a company on PowerOffice Go or Tripletex, a month at a time (#183, #187) */}
        {!dept && span === 'month' && tenant?.payroll_system === 'poweroffice' && (
          <PowerOfficeRevenue ym={ym} target={target} />
        )}
        {!dept && span === 'month' && tenant?.payroll_system === 'tripletex' && (
          <PowerOfficeRevenue ym={ym} target={target} sys={TRIPLETEX} />
        )}
        <table className="mt lcards" data-testid="omsetning">
          <caption className="sr">
            {T('{basis} beregning', { basis: label })}, {dept ? dept.name : tenant.name}
          </caption>
          <thead>
            <tr>
              <th>{T('Dag')}</th>
              {!dept && <th className="num">{T('Lønn %')}</th>}
              <th className="num">{T('{basis} tid', { basis: label })}</th>
              <th className="num">{T('{basis} kostnad', { basis: label })}</th>
              <th className="num">{T('Omsetning')}</th>
              <th>
                <span className="sr">{T('Dagen')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.d}>
                <td className="lday">
                  {DNL[dow(r.d)]} {fmtDs(r.d)}
                </td>
                {!dept && (
                  <td
                    className="num"
                    data-testid="day-pct"
                    data-k={T('Lønn %')}
                    aria-label={r.state === 'missing' && !r.future ? T('Omsetning mangler') : undefined}
                    id={r.state === 'odd' ? `odd-${r.d}` : undefined}
                  >
                    {r.state === 'odd' ? (
                      <span className="lodd">{T('Sjekk beløpet')}</span>
                    ) : r.state === 'zero' ? (
                      <span className="lmiss">{T('0 kr omsetning')}</span>
                    ) : r.pct == null ? (
                      !known || r.future ? (
                        '–'
                      ) : r.none && r.state === 'ok' ? (
                        <span className="lmiss">{T('Ingen vakter')}</span>
                      ) : (
                        <span className="lunk">{T('Ukjent')}</span>
                      )
                    ) : (
                      <span className={r.pct > target ? 'lover' : undefined}>{pctText(r.pct)}</span>
                    )}
                  </td>
                )}
                <td className="num" data-k={T('{basis} tid', { basis: label })}>
                  {dash(`${num(r.h)} t`)}
                </td>
                <td className="num" data-k={T('{basis} kostnad', { basis: label })}>
                  {dash(`${kr(r.cost)} kr`)}
                </td>
                <td className="num" data-k={T('Omsetning')}>
                  {/* whole kroner with the thousands spaced, as every other amount (#263 O-05) */}
                  <span className="lkr">
                    <input
                      className="inp"
                      type="text"
                      inputMode="numeric"
                      aria-label={`Omsetning ${fmtDs(r.d)}`}
                      aria-describedby={r.state === 'odd' ? `odd-${r.d}` : undefined}
                      placeholder={salesQ.ready && !r.future ? T('Mangler') : ''}
                      // a day typed before the saved figures arrive would overwrite one it never saw
                      disabled={!salesQ.ready}
                      key={`${r.d}-${r.sales ?? 'none'}`}
                      defaultValue={r.sales == null ? '' : kr(r.sales)}
                      onBlur={(e) => {
                        const v = typedSales(e.target.value);
                        if (v === (r.sales ?? null)) return;
                        // whole kroner: "1" read as 1 kr and gave 876 298 % (owner, 2026-09-27)
                        if (v != null && v > 0 && v < 1000) {
                          toast(T('Skriv hele kronebeløpet, f.eks. 32 000.'));
                          return;
                        }
                        setSales(r.d, v);
                      }}
                    />
                    <span aria-hidden="true">kr</span>
                  </span>
                </td>
                <td>
                  {/* the plan's day for planned figures; the day's registrations for recorded and approved (#263) */}
                  {basis === 'planned' ? (
                    <Link
                      className="mlink"
                      to={`/dagen?d=${r.d}`}
                      aria-label={T('Planen for {d}', { d: fmtDs(r.d) })}
                    >
                      <span className="lgo">{T('Dagen')}</span>
                      <Icon name="chevronRight" />
                    </Link>
                  ) : (
                    <Link
                      className="mlink"
                      to={`/timeliste?uke=${mondayOf(r.d)}&vis=${basis === 'approved' ? 'godkjent' : 'alle'}`}
                      aria-label={T('Registreringene for uke {n}', { n: isoWeek(r.d) })}
                    >
                      <span className="lgo">{T('Registreringer')}</span>
                      <Icon name="chevronRight" />
                    </Link>
                  )}
                </td>
              </tr>
            ))}
            {/* the ratio's own days, then every day: two totals, never one mixed formula (V4, V5) */}
            {!dept && (
              <tr className="tot lmatched">
                <td>
                  {T('Dager med omsetning')}{' '}
                  <small>
                    {!known ? '–' : counted.length === 1 ? T('1 dag') : T('{n} dager', { n: counted.length })}
                  </small>
                </td>
                {!dept && (
                  <td className="num" data-k={T('Lønn %')}>
                    {wk == null ? '–' : pctText(wk)}
                  </td>
                )}
                <td className="num" data-k={T('{basis} tid', { basis: label })}>
                  {dash(`${num(counted.reduce((a, r) => a + r.h, 0))} t`)}
                </td>
                <td className="num" data-k={T('{basis} kostnad', { basis: label })}>
                  {dash(`${kr(cc)} kr`)}
                </td>
                <td className="num" data-k={T('Omsetning')}>
                  {counted.length ? `${kr(cs)} kr` : '–'}
                </td>
                <td />
              </tr>
            )}
            <tr className="tot lall">
              <td>
                {T('Alle dager')} <small>{period}</small>
              </td>
              {!dept && (
                <td className="num" data-k={T('Lønn %')}>
                  {!known ? (
                    '–'
                  ) : counted.length < rows.length ? (
                    <span className={missing ? 'lunk' : 'lmiss'}>
                      {T('{a} av {b} dager', { a: counted.length, b: rows.length })}
                    </span>
                  ) : wk == null ? (
                    '–'
                  ) : (
                    pctText(wk)
                  )}
                </td>
              )}
              <td className="num" data-k={T('{basis} tid', { basis: label })}>
                {dash(`${num(rows.reduce((a, r) => a + r.h, 0))} t`)}
              </td>
              <td className="num" data-k={T('{basis} kostnad', { basis: label })}>
                {dash(`${kr(tc)} kr`)}
              </td>
              <td className="num" data-k={T('Omsetning')}>
                {rows.every((r) => r.sales == null) ? '–' : `${kr(ts)} kr`}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
        <Explain q={T('Beregningsgrunnlag og avgrensning')} className="lcalc">
          <p>
            {T(
              'Lønnskostnaden er timer ganger timesats, inkl. {holiday} % feriepenger, {aga} % arbeidsgiveravgift og {pension} % pensjon fra Innstillinger, Lønn; for fastlønn regnes timesatsen fra månedslønnen og avtalt uke.',
              {
                holiday: num(rates.holidayPct),
                aga: num(rates.agaPct),
                pension: num(rates.pensionPct),
              },
            )}
          </p>
          <p>
            {T(
              'Planlagt: hele vakten fra planlagt start til planlagt slutt, før pause; kostnaden etter ubetalt pause etter bedriftens pauseregel. Stemplinger endrer ikke tallene, og stempling uten planlagt vakt er ikke med. Registrert: tiden mellom stempling inn og ut, før pause; kostnaden etter ubetalt pause. Godkjent: bare godkjente vakter, arbeidstiden etter ubetalt pause, slik Lønnsgrunnlag teller den.',
            )}
          </p>
          <p>
            {T(
              'En vakt over midnatt telles på dagen den begynner. Omsetningen legges inn per dag i hele kroner og lagres for hele bedriften. Tomt felt betyr at omsetningen mangler; 0 er registrert null. Et beløp under 1 000 kr må kontrolleres før dagen telles. Prosenten regnes bare på dager med omsetning: både kostnaden og omsetningen for en dag uten omsetning holdes utenfor. Alle dager viser samlet tid og kostnad, også for dager som mangler omsetning.',
            )}
          </p>
          <p>
            {/* V7.4 (#281): the share is a cost ratio on the covered days, nothing more */}
            {T(
              'Lønnsprosenten er lønnskostnad delt på omsetning for de samme dagene. Den sier ikke noe om fortjeneste, og den er ikke en vurdering av om lov eller tariff er fulgt.',
            )}
          </p>
        </Explain>
      </TabPanel>
    </div>
  );
}
